import React, { useState } from "react";
import {
  BriefcaseBusiness,
  Calendar,
  Clock2,
  MoveRight,
  Star,
  X,
} from "lucide-react";
import StudentDashboard from "../../../layout/StudentDashboard";
import { Link } from "react-router-dom";
import { useGetTutorsQuery } from "../../../hooks/api/Students/Tutors/useGetTutorsQuery";
import { useGetStudentQuery } from "../../../hooks/api/Students/Auth/useGetStudentQuery";
import AvailabilityBadge from "../Tutors/Components/AvailabilityBadge";
import Loader from "../../../components/Loader";
import { Drawer, Tabs } from "antd";
import { useGetTutorQuery } from "../../../hooks/api/Students/Tutors/useGetTutorQuery";
import EmptyState from "../../../components/EmptyState";
import TutorBookingStepper from "../Tutors/Components/TutorBookingStepper";

const StudentOverview = () => {
  const { data: student } = useGetStudentQuery();
  const { data: tutors, isLoading } = useGetTutorsQuery({});
  const [id, setId] = useState("");
  const [visible, setVisible] = useState(false);

  const { data: tutorsData, isLoading: tutorsLoading } = useGetTutorQuery({
    id,
  });

  const formatSessionTime = (minutes: number = 0): string => {
    if (minutes >= 1440) {
      const days = Math.round(minutes / 1440);
      return `${days} Day${days > 1 ? "s" : ""}`;
    } else if (minutes >= 60) {
      const hours = Math.round(minutes / 60);
      return `${hours} Hour${hours > 1 ? "s" : ""}`;
    } else {
      return `${minutes} Minute${minutes === 1 ? "" : "s"}`;
    }
  };

  return (
    <StudentDashboard>
      <div className="container md:px-6 px-0">
        <p className="text-txs text-gray900 W2STBold md:mt-0 mt-3">
          Welcome
          {student?.data?.firstName && ", " + student?.data?.firstName}
        </p>
        <div className="pb-6 border-b-[1px] border-b-gray300 text-tmd">
          <span className="text-gray600">No upcoming sessions, </span>
          <Link
            to="/student/tutors"
            className="text-primary500 underline underline-offset-2 cursor-pointer"
          >
            let's find you a tutor
          </Link>
        </div>
        <div className="md:flex grid gap-4 md:mt-10 mt-3 mb-10">
          <div className="rounded-lg p-3 border-[1px] bordeer-gray100  md:w-4/12 w-full bg-white">
            <div className="flex gap-4 items-center text-primary600 mb-2">
              <span className="rounded-full bg-primary50 p-4 md:text-[30px] text-[20px]]">
                <Calendar />
              </span>
              <span className="text-gray900 W2STMedium text-tmd">
                Sessions booked
              </span>
            </div>
            <p className="text-gray900 W2STBold ml-2 md:text-3xl text-lg">
              {student?.data?.sessionsBooked || 0}
            </p>
          </div>
          <div className="rounded-lg p-3 border-[1px] bordeer-gray100  md:w-4/12 w-full bg-white">
            <div className="flex gap-4 items-center text-bluelight600 mb-2">
              <span className="rounded-full bg-bluelight50 p-4 md:text-[30px] text-[20px]">
                <Clock2 />
              </span>
              <span className="text-gray900 W2STMedium text-tmd">
                Total session time
              </span>
            </div>
            <p className="text-gray900 W2STBold ml-2 md:text-3xl text-lg">
              {formatSessionTime(student?.data?.totalSessionTime ?? 0)}{" "}
              {student?.data?.totalSessionTime > 0 && (
                <span className="ml-2 W2STLight text-[14px]">
                  ({student?.data?.totalSessionTime} Mins)
                </span>
              )}
            </p>
          </div>
          <div className="rounded-lg p-3 border-[1px] bordeer-gray100  md:w-4/12 w-full bg-white">
            <div className="flex gap-4 items-center text-warning600 mb-2">
              <span className="rounded-full bg-warning50 p-4 md:text-[30px] text-[20px]">
                <Star />
              </span>
              <span className="text-gray900 W2STMedium text-tmd">
                Average rating
              </span>
            </div>
            <p className="text-gray900 W2STBold ml-2 md:text-3xl text-lg">0</p>
          </div>
        </div>
        <p className="text-txl text-gray900 W2STBold">Suggested Tutors</p>
        <div className="pb-6 border-b-[1px] border-b-gray300 md:flex justify-between">
          <div>
            <span className="text-gray600">
              The suggestions are based on your location{" "}
            </span>
          </div>
          <Link
            to="/student/tutors"
            className="flex gap-3 text-gray500 items-center"
          >
            See more <MoveRight size={20} />
          </Link>
        </div>
        <div className="mb-[100px]">
          {isLoading && <Loader />}
          {tutors && tutors?.data?.docs.length === 0 && (
            <EmptyState
              title="There are no tutors yet."
              desc="Verified tutors would display here soon"
            />
          )}
          <div className="md:grid md:grid-cols-3 gap-4 mt-5">
            {tutors &&
              tutors?.data?.docs.length > 0 &&
              !isLoading &&
              tutors?.data?.docs
                .slice(0, 3)
                .map(
                  ({
                    firstName,
                    lastName,
                    professionalTitle,
                    availabilityType,
                    id,
                    hourlyRate,
                    profilePicture,
                  }: any) => (
                    <div
                      key={id}
                      className="bg-white rounded-md shadow-sm overflow-hidden cursor-pointer"
                      onClick={() => {
                        setId(id);
                        setVisible(true);
                      }}
                    >
                      <img
                        src={profilePicture ? profilePicture : ""}
                        alt="tutor avatar"
                        className="h-[218px] rounded-t-lg w-full"
                      />
                      <div className="p-[16px]">
                        <h3 className="W2STMedium mb-[5px] text-gray900 md:text-[18px] text-[14px]">
                          {firstName + " " + lastName}
                        </h3>
                        <div className="mt-2">
                          <div className="flex gap-3 items-center">
                            <BriefcaseBusiness
                              style={{ color: "text-gray700" }}
                            />
                            <span className="text-gray500 text-sm">
                              {professionalTitle}
                            </span>
                          </div>
                        </div>

                        {/* <div className="mt-2 bg-warning50 rounded-md flex gap-2 items-center p-2 text-sm">
                          <Award />
                          <span className="text-gray500">
                            15 sessions (5 reviews)
                          </span>
                        </div> */}
                        <AvailabilityBadge
                          availabilityType={availabilityType}
                        />
                        <div className="flex justify-between mt-[16px]">
                          <div>
                            <p className="W2STMedium text-gray900">
                              ${hourlyRate}/hr
                            </p>
                            <h3 className="text-gray500">Hourly rate</h3>
                          </div>
                          <button className="text-white px-[27px] py-[10px] rounded-md bg-primary900 W2STMedium">
                            Book Tutor
                          </button>
                        </div>
                      </div>
                    </div>
                  )
                )}
          </div>
          <Drawer
            width={window.innerWidth > 768 ? 600 : "100%"}
            title={
              <div className="flex justify-between items-center">
                <h3>Tutor Details</h3>
                <X
                  onClick={() => setVisible(false)}
                  className="cursor-pointer"
                />
              </div>
            }
            open={visible}
            closable={false}
            maskClosable={false}
          >
            {tutorsLoading && <Loader />}
            {!tutorsLoading && tutorsData && (
              <div>
                <div className="flex gap-2 mb-6">
                  <img
                    src={tutorsData?.data?.profilePicture}
                    className="h-[60px] rounded-full w-[60px]"
                    alt="avatar"
                  />
                  <div className="flex-1">
                    <h3 className="W2STMedium mb-[5px] text-gray900 md:text-[18px] text-[14px]">
                      {tutorsData?.data?.firstName +
                        " " +
                        tutorsData?.data?.lastName}
                    </h3>
                    <div className="mt-2">
                      <div className="flex gap-3 items-center">
                        <BriefcaseBusiness
                          size={20}
                          style={{ color: "text-gray700" }}
                        />
                        <span className="text-gray500">
                          {tutorsData?.data?.professionalTitle}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <Tabs defaultActiveKey="1" className="w-full">
                  <Tabs.TabPane
                    tab={
                      <span className="flex items-center gap-2">
                        <Calendar size={16} />
                        Book Session
                      </span>
                    }
                    key="1"
                  >
                    <div className="py-4">
                      <TutorBookingStepper id={id} setVisible={setVisible} />
                    </div>
                  </Tabs.TabPane>

                  <Tabs.TabPane
                    tab={
                      <span className="flex items-center gap-2">
                        <BriefcaseBusiness size={16} />
                        Experience
                      </span>
                    }
                    key="2"
                  >
                    <div className="py-4 space-y-4">
                      {tutorsData?.data?.workExperience?.map((exp: any) => (
                        <div
                          key={exp._id}
                          className="bg-gray-50 p-4 rounded-lg"
                        >
                          <div className="flex justify-between items-start">
                            <h5 className="font-medium text-gray-900">
                              {exp.title}
                            </h5>
                            <span className="text-sm text-gray-500">
                              {new Date(exp.startDate).toLocaleDateString(
                                "en-US",
                                {
                                  month: "short",
                                  year: "numeric",
                                }
                              )}{" "}
                              -{" "}
                              {exp.endDate
                                ? new Date(exp.endDate).toLocaleDateString(
                                    "en-US",
                                    {
                                      month: "short",
                                      year: "numeric",
                                    }
                                  )
                                : "Present"}
                            </span>
                          </div>
                          <p className="text-gray-600 text-sm mt-1">
                            {exp.company}
                          </p>
                          <p className="text-gray-500 text-sm mt-2">
                            {exp.description}
                          </p>
                        </div>
                      ))}
                      {(!tutorsData?.data?.workExperience ||
                        tutorsData?.data?.workExperience.length === 0) && (
                        <div className="text-center text-gray-500 py-8">
                          No work experience listed
                        </div>
                      )}
                    </div>
                  </Tabs.TabPane>

                  <Tabs.TabPane
                    tab={
                      <span className="flex items-center gap-2">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
                        </svg>
                        Education
                      </span>
                    }
                    key="3"
                  >
                    <div className="py-4 space-y-4">
                      {tutorsData?.data?.education?.map((edu: any) => (
                        <div
                          key={edu._id}
                          className="bg-gray-50 p-4 rounded-lg"
                        >
                          <div className="flex justify-between items-start">
                            <h5 className="font-medium text-gray-900">
                              {edu.college}
                            </h5>
                            <span className="text-sm text-gray-500">
                              {new Date(edu.enrolled).toLocaleDateString(
                                "en-US",
                                {
                                  month: "short",
                                  year: "numeric",
                                }
                              )}{" "}
                              -{" "}
                              {edu.graduated
                                ? new Date(edu.graduated).toLocaleDateString(
                                    "en-US",
                                    {
                                      month: "short",
                                      year: "numeric",
                                    }
                                  )
                                : "Present"}
                            </span>
                          </div>
                          <p className="text-gray-600 text-sm mt-1">
                            {edu.degree} in {edu.majors}
                          </p>
                        </div>
                      ))}
                      {(!tutorsData?.data?.education ||
                        tutorsData?.data?.education.length === 0) && (
                        <div className="text-center text-gray-500 py-8">
                          No education history listed
                        </div>
                      )}
                    </div>
                  </Tabs.TabPane>
                </Tabs>
              </div>
            )}
          </Drawer>
        </div>
      </div>
    </StudentDashboard>
  );
};

export default StudentOverview;
